/**
 * Wspólny limit rozmiaru uploadu (100 MB / plik) + walidacja i formatowanie.
 *
 * Egzekwowany klient-side we WSZYSTKICH miejscach, gdzie użytkownik dodaje pliki
 * (załączniki, logo/podpis, importy, odczyty AI z dokumentu/zdjęcia itd.).
 * Serwer (spring `max-file-size`) trzyma zapas 250 MB — 100 MB jest limitem produktowym,
 * a nie technicznym, więc pilnujemy go głównie tu, żeby dać czytelny komunikat zamiast HTTP 413.
 *
 * Wyjątek: masowy import katalogu ({@code BulkClassificationModal}) ma własny, wyższy
 * limit 250 MB/plik i 2 GB/paczkę — tam wgrywa się całe foldery.
 */
export const MAX_UPLOAD_MB = 100;
export const MAX_UPLOAD_BYTES = MAX_UPLOAD_MB * 1024 * 1024;

/** Postęp wysyłki jednego żądania upload. */
export interface UploadProgress {
  /** Bajty wysłane do serwera. */
  loaded: number;
  /** Rozmiar całości żądania. */
  total: number;
  /** 0–100 (zaokrąglone). */
  percent: number;
}

/** Czytelny rozmiar pliku (B / KB / MB). */
export function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * Sprawdza rozmiar plików przed wysyłką. Zwraca gotowy komunikat błędu (PL), gdy
 * którykolwiek plik przekracza {@link MAX_UPLOAD_MB} MB — w przeciwnym razie `null`.
 *
 * Pustość plików (0 B / pusty wybór) NIE jest tu traktowana jako błąd rozmiaru —
 * tę walidację robią wywołujący tam, gdzie ma sens.
 */
export function validateUploadSize(
  input: File | FileList | File[] | null | undefined,
): string | null {
  if (!input) return null;
  const files: File[] = input instanceof File ? [input] : Array.from(input as ArrayLike<File>);
  const oversized = files.filter(f => f && f.size > MAX_UPLOAD_BYTES);
  if (oversized.length === 0) return null;
  const names = oversized.slice(0, 3).map(f => `${f.name} (${fmtBytes(f.size)})`).join(', ');
  const more = oversized.length > 3 ? ` (+${oversized.length - 3} więcej)` : '';
  const noun = oversized.length === 1 ? 'Plik przekracza' : `${oversized.length} plików przekracza`;
  return `${noun} limit ${MAX_UPLOAD_MB} MB na pojedynczy plik: ${names}${more}. `
    + 'Zmniejsz lub podziel plik przed wysłaniem.';
}
